import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface AnalysisMessage {
  id: number | string
  message: string
  streaming: boolean
  user: string
  filters?: GameAnalysisFilters
  llm_provider?: string
  llm_model_name?: string
  created_at?: string
}

export interface GameAnalysisFilters {
  player_ids: number[]
  team_ids: number[]
  phase_ids: number[]
  round_numbers: number[]
}

interface GameAnalysisState {
  messages: AnalysisMessage[]
  isConnected: boolean
  error: string | null
  isGenerating: boolean
  selectedFilters: GameAnalysisFilters
}

const initialState: GameAnalysisState = {
  messages: [],
  isConnected: false,
  error: null,
  isGenerating: false,
  selectedFilters: {
    player_ids: [],
    team_ids: [],
    phase_ids: [],
    round_numbers: [],
  },
}

const gameAnalysisSlice = createSlice({
  name: 'gameAnalysis',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },
    addMessage: (state, action: PayloadAction<AnalysisMessage>) => {
      if (action.payload.streaming) {
        if (state.messages.length > 0) {
          const lastMessage = state.messages[state.messages.length - 1]
          if (lastMessage.streaming) {
            lastMessage.message += action.payload.message
            if (action.payload.created_at !== undefined) {
              lastMessage.created_at = action.payload.created_at
            }
            if (action.payload.llm_provider !== undefined) {
              lastMessage.llm_provider = action.payload.llm_provider
            }
            if (action.payload.llm_model_name !== undefined) {
              lastMessage.llm_model_name = action.payload.llm_model_name
            }
            return
          }
        }
        state.messages.push(action.payload)
      } else if (!action.payload.streaming) {
        if (
          state.messages.length > 0 &&
          state.messages[state.messages.length - 1].streaming
        ) {
          state.messages[state.messages.length - 1] = {
            ...action.payload,
            streaming: false,
          }
        } else {
          state.messages.push(action.payload)
        }
      } else {
        state.messages.push(action.payload)
      }
    },
    setMessages: (state, action: PayloadAction<AnalysisMessage[]>) => {
      state.messages = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    setIsGenerating(state, action: PayloadAction<boolean>) {
      state.isGenerating = action.payload
    },
    setFilters: (state, action: PayloadAction<GameAnalysisFilters>) => {
      state.selectedFilters = action.payload
    },
    clearFilters: (state) => {
      state.selectedFilters = {
        player_ids: [],
        team_ids: [],
        phase_ids: [],
        round_numbers: [],
      }
    },
    stopStreamingMessages: (state) => {
      if (state.messages.length > 0) {
        const lastMessage = state.messages[state.messages.length - 1]
        if (lastMessage.streaming) {
          lastMessage.streaming = false
        }
      }
    },
  },
})

export const {
  setConnectionStatus,
  addMessage,
  setMessages,
  setError,
  setIsGenerating,
  setFilters,
  clearFilters,
  stopStreamingMessages,
} = gameAnalysisSlice.actions

export default gameAnalysisSlice.reducer
